import { useDeviceType } from '@cian/ui-kit/responsive/hooks/useDeviceType';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cian/frontend-reviews-components';
import { IconDislike16, IconLike16 } from '@cian/ui-kit';

import { setAnswersPage, setAnswersVisibility, setReviewReaction } from '../../../actions/reviews';
import { ButtonLink } from '../../../components/ButtonLink';
import { Footer } from '../../../components/Footer';
import { getReviewById } from '../../../selectors/reviews';
import { EStatus } from '../../../types/likes';
import { ReviewPopupContainer } from '../../ReviewPopup';
import { requestAuthentication } from '../../../actions/authentication';
import { getIsAuthenticated } from '../../../selectors/user';
import { loadAnswers } from '../../../actions/answers/loadAnswers';
import { NewAnswerButtonContainer } from '../../NewAnswerButton';
import { trackOpenThread } from '../../QuestionItem/tracking';
import { getReviewsConfig } from '../../../utils/getReviewsConfig/getReviewsConfig';
import { TThunkDispatch } from '../../../types/redux';

interface IFooterContainerProps {
  reviewId: string;
  inPopup?: boolean;
}

export function FooterContainer({ reviewId }: IFooterContainerProps) {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isDesktop = deviceType === 'desktop';
  const [reviewPopupId, setReviewPopupId] = React.useState<string | null>(null);
  const { like, answersCount, areAnswersOpened, hasAnswers } = useSelector(getReviewById(reviewId));
  const { answers, showAnswerButton } = useSelector(getReviewById(reviewId));
  const reviewsConfig = getReviewsConfig();
  const alwaysShowAnswersUnderReview = reviewsConfig.ALWAYS_SHOW_ANSWERS_UNDER_REVIEW;

  const onAnswerVisibilityClick = React.useCallback(() => {
    if (!areAnswersOpened || !isDesktop) {
      const page = {
        siteType: deviceType,
        offerType: 'JK',
        pageType: 'ReviewsJK',
        extra: {
          review_id: reviewId,
        },
      };

      trackOpenThread(page);
    }

    if (isDesktop || alwaysShowAnswersUnderReview) {
      dispatch(setAnswersVisibility({ reviewId, state: !areAnswersOpened }));
      if (!areAnswersOpened && !answers.length) {
        dispatch(loadAnswers({ reviewId, page: 0 }));
      }
    } else {
      setReviewPopupId(reviewId);
    }
  }, [dispatch, areAnswersOpened, reviewId, isDesktop]);

  const onReviewPopupClose = React.useCallback(() => {
    setReviewPopupId(null);
    dispatch(setAnswersPage({ reviewId, page: 0 }));
  }, [dispatch, reviewId]);

  const setReviewLike = React.useCallback(() => setReviewReaction({ reviewId, type: EStatus.Liked }), []);
  const setReviewDisliked = React.useCallback(() => setReviewReaction({ reviewId, type: EStatus.Disliked }), []);

  const onLikeClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setReviewLike());
    } else {
      dispatch(requestAuthentication({ onSuccess: setReviewLike }));
    }
  }, [dispatch, isAuthenticated, setReviewLike, requestAuthentication]);

  const onDislikedClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setReviewDisliked());
    } else {
      dispatch(requestAuthentication({ onSuccess: setReviewDisliked }));
    }
  }, [dispatch, isAuthenticated, setReviewDisliked, requestAuthentication]);

  const showRateButtons = reviewsConfig.SHOW_LIKE_BUTTONS;

  const likeJsx = showRateButtons ? (
    <IconButton
      Icon={IconLike16}
      active={like.status === EStatus.Liked}
      onClick={onLikeClick}
      label={like.likesCount}
    />
  ) : null;

  const dislikeJsx = showRateButtons ? (
    <IconButton
      Icon={IconDislike16}
      active={like.status === EStatus.Disliked}
      onClick={onDislikedClick}
      label={like.dislikesCount}
    />
  ) : null;

  const answerButton = showAnswerButton ? <NewAnswerButtonContainer type="review" targetId={reviewId} /> : null;

  return (
    <>
      <Footer
        like={likeJsx}
        dislike={dislikeJsx}
        answer={answerButton}
        answerVisibility={
          hasAnswers && (
            <ButtonLink onClick={onAnswerVisibilityClick} testId="AnswerVisibilityControl">
              {areAnswersOpened ? 'Скрыть ответы' : answersCount}
            </ButtonLink>
          )
        }
      />
      {reviewPopupId !== null && <ReviewPopupContainer reviewId={reviewPopupId} onClose={onReviewPopupClose} />}
    </>
  );
}
